import React, { ChangeEvent, useState, useEffect, useCallback, useMemo } from 'react';
import { Autocomplete, Box, Divider, Grid, TextField , Dialog, DialogTitle, DialogContent, DialogActions, 
  Button, FormControl, FormControlLabel, Checkbox, FormGroup, InputLabel, Select, MenuItem, SelectChangeEvent, 
  Tabs, Tab, Radio, FormHelperText, IconButton,
  Typography,
  RadioGroup} from '@mui/material';
import { MuiColorInput } from 'mui-color-input';
import { DataGrid, GridToolbar, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { defaultColorMap, colorScaleOptions, getColorForValue } from '../utilities/Colors';
import _, { filter } from 'lodash';
import { PinRounded, CloseRounded, TrafficRounded } from '@mui/icons-material';
import { PlotData } from 'plotly.js';
import AnnotationManager from './AnnotationManager';
import { deepEqual } from '../utilities/utils';

const MarkerSymbols: { [key: string]: string } = {
  0: 'circle',
  1: 'square',
  2: 'diamond',
  3: 'cross',
  4: 'x',
  5: 'triangle-up',
  6: 'triangle-down',
  7: 'triangle-left',
  8: 'triangle-right',
  9: 'triangle-ne',
  10: 'triangle-se',
  11: 'triangle-sw',
  12: 'triangle-nw',
  13: 'pentagon',
  14: 'hexagon',
  15: 'hexagon2',
  16: 'octagon',
  17: 'star',
  18: 'hexagram',
  19: 'star-triangle-up',
  20: 'star-triangle-down',
  21: 'star-square',
  22: 'star-diamond',
  23: 'diamond-tall',
  24: 'diamond-wide',
  25: 'hourglass',
  26: 'bowtie',
  27: 'circle-cross',
  28: 'circle-x',
};

const defaultSymbolMap = MarkerSymbols;

interface PlotOptionsModalProps {
  plotConfig: PlotConfig;
  plotLayers: any[];
  setPlotConfigs: (config: { [key: string]: PlotConfig }, save: boolean) => void;
  isOpen: boolean;
  onClose: () => void;
  disableSave: boolean;
  defaultConfig: PlotConfig;
  plotTypeOptions: {label: string, id: number, value: string}[];
}

const PlotOptionsModal: React.FC<PlotOptionsModalProps> = ({
  plotConfig,
  plotLayers,
  setPlotConfigs,
  isOpen,
  onClose,
  disableSave,
  defaultConfig,
  plotTypeOptions,
}) => {

  const [config, setConfig] = useState<PlotConfig>(defaultConfig);
  const [tab, setTab] = useState<number>(0);
  const [annotations, setAnnotations] = useState<PlotlyAnnotation[]>([]);
  const [shapes, setShapes] = useState<PlotlyShape[]>([]);


  useEffect(() => { 
    setConfig((prevConfig) => ({
      ...prevConfig,  // Necessary for setting defaults above, especially for new setting values that have been added and don't exist in previously saved older plots
      ...plotConfig,
      colorMap: plotConfig.colorMap || defaultColorMap,
      symbolMap: plotConfig.symbolMap || defaultSymbolMap,
      // colorOverride: plotConfig.colorOverride || {},
    }));
  }, [plotConfig, isOpen]);

  const handleAnnotationUpdate = useCallback((updatedAnnotations: PlotlyAnnotation[], updatedShapes: PlotlyShape[]) => {
    setAnnotations(updatedAnnotations);
    setShapes(updatedShapes);
  }, []);

  const handleChange = useCallback(
    (key: keyof PlotConfig, value: any) => {
      setConfig((prevConfig) => ({ ...prevConfig, [key]: value }));
    },
    [setConfig]
  );

  const debouncedChange = useCallback(
    _.debounce((key: keyof PlotConfig, value: any) => handleChange(key, value), 500),
    [handleChange]
  );

  const handleApply = (save: boolean) => {
    const updatedConfig = {
      ...config,
      annotations,
      shapes,
    };

    setPlotConfigs({ 'plot1': updatedConfig }, save);
    onClose();
  };

  const handleSavePlot = () => {
    handleApply(true);
  };

  const getTraceColor = useCallback((id: number, traceName: string, filterDefaultColorMap: string[], trace: Trace ) => {
    let mod_index = 0;

    if (config.colorOverride && config.colorOverride[traceName]) {
      return {color:config.colorOverride[traceName], override:true}; // use color override if available
    }
    else {
      let autoAssign = filterDefaultColorMap[id % filterDefaultColorMap.length];
      // let traceColor = trace.marker.color;
      let traceColor = trace?.marker?.color ?? undefined;

      if (Array.isArray(traceColor)){
        traceColor = trace.marker.color[0];
      }
      else if (!traceColor) {
        traceColor = autoAssign;
      }
      return {color: traceColor, override:false};
    }
    // if (config.colorMap) {
    //   mod_index = id % Object.keys(config.colorMap).length;
    //   return {color:config.colorMap[mod_index], override:true} ;
    // } else {
    //   return {color:plotConfig.colorMap ? plotConfig.colorMap[mod_index] : '#000000', override:true}; // just return last defined color as fall back
    // }
  }, [plotLayers, config.colorMap, config.colorOverride, plotConfig.colorMap, plotConfig.colorOverride]);


  const handleToggleOverride = (params: any) => {

    if (params.row.override) {
      handleChange('colorOverride', {...config.colorOverride, [params.row.name]: undefined})
    }
    else {
      handleChange('colorOverride', {...config.colorOverride, [params.row.name]: params.row.color})
    }
  }

  const traceColumns = [
    { field: 'id', headerName: 'Trace', width: 50 },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      renderCell: (params: any) => <Box sx={{ fontSize: '15px' }}>{params.row.name}</Box>,
    },
    {
      field: 'type',
      headerName: 'Type',
      // flex: 1,
      // width: 100,
      ...(config.plotType !== 'scatter' ? {flex: 1} : {width: 100}),
      renderCell: (params: any) => <Box sx={{ fontSize: '15px' }}>{params.row.type}</Box>,
    },
    {
      field: 'color',
      headerName: 'Color',
      flex: 1,
      renderCell: (params: any) => (
        <MuiColorInput
          sx={{ width: '100%' }}
          value={params.row.color}
          format="hex"
          isAlphaHidden={true}
          onChange={(value, colors) => {
            let color = value;
            if (params.row.override) {
              const newColorOverride = structuredClone(config.colorOverride) || {}
              newColorOverride[params.row.name] = color;
              newColorOverride[params.row.name.trim()] = color;
              handleChange('colorOverride', newColorOverride);
            } else {
              const newColorMap = { ...config.colorMap, [params.row.colorMapIndex]: color };
              handleChange('colorMap', newColorMap);
            }
            // debouncedChange('colorMap', { ...config.colorMap, [params.row.colorMapIndex]: color })
            // debouncedChange('colorOverride', {...config.colorOverride, [params.row.name]: color})
          }}
          // Consider updating code to differentiate color picker callback (using onBlur) vs. typing into color field
        />
      ),
    },
    // {
    //   field: 'override',
    //   headerName: 'Lock',
    //   // flex: 1,
    //   // width: 50,
    //   ...(config.plotType !== 'scatter' ? {flex: 1} : {width: 50}),
    //   renderCell: (params: any) => (
    //       <Checkbox
    //         // labelId={`override-${params.row.id}`}4
    //         sx={{minWidth:undefined}}
    //         id={`override-${params.row.id}`}
    //         checked={params.row.override}
    //         onChange={(event) => handleToggleOverride(params)}
    //       />
    //   ),
    // },
    // {
    //   field: 'colorMapIndex',
    //   headerName: 'Index',
    //   // flex: 1,
    //   // width: 50,
    //   ...(config.plotType !== 'scatter' ? {flex: 1} : {width: 50}),
    //   renderCell: (params: any) => <Box sx={{ fontSize: '15px' }} >{params.row.colorMapIndex}</Box>,
    // },
    
    ...(config.plotType === 'scatter'
      ? [
          {
            field: 'symbol',
            headerName: 'Symbol',
            flex: 1,
            renderCell: (params: any) => (
              <Autocomplete
                id="plotOptionsModal"
                autoComplete
                disableClearable
                selectOnFocus
                clearOnBlur
                sx={{ width: '100%' }}
                defaultValue={{ label: MarkerSymbols[String(params.row.id)], id: String(params.row.id) }}
                options={Object.keys(MarkerSymbols).map((key) => ({
                  label: MarkerSymbols[key],
                  id: key,
                }))}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                    }}
                    variant="standard"
                    fullWidth
                  />
                )}
                onChange={(_, value) => handleChange('symbolMap', { ...config.symbolMap, [params.row.id]: value.id })}
              />
            ),
          },
          {
            field: 'fill',
            headerName: 'Fill',
            flex: 1,
            renderCell: (params: any) => (
              <FormControl fullWidth>
                <InputLabel id={`fill-label-${params.row.id}`}>Fill</InputLabel>
                <Select
                  labelId={`fill-label-${params.row.id}`}
                  id={`fill-select-${params.row.id}`}
                  value={config.fillMap ? config.fillMap[params.row.id] || 'false' : 'false'}
                  onChange={(event) => handleChange('fillMap', { ...config.fillMap, [params.row.id]: event.target.value })}
                >
                  <MenuItem value="true">True</MenuItem>
                  <MenuItem value="false">False</MenuItem>
                </Select>
              </FormControl>
            ),
          },
        ]
      : []),
  ];

  const colorScatterByOptions = (): string[] => {

    const extractHoverKeys = (text: string): string[] => {
      if (text !== undefined) {
        const keyValuePairs = text.split('<br />');
        const result: string[] = [];
        keyValuePairs.forEach(pair => {
            const [key, value] = pair.split(':').map(part => part.trim());
            if (key && value && key !== 'Value' && key != 'X' && key != 'Y') {
                result.push(key); // Store key-value pairs, ignoring specified keys
            }
        });
        return result;
      }
      return [];
    };

    let hoverKeys: any = [];
    // examine first trace and hovertext available for first point
    if (plotLayers?.[0]?.hovertext && Object.keys(plotLayers[0]).includes('hovertext')) {
      hoverKeys = extractHoverKeys(plotLayers[0].hovertext[0]);
    }
       
    
    // examine first trace and hovertext available
    let options: string[] = hoverKeys;
    
    if (options == undefined) {
      options = [''];
    }
    // console.log("color by options: ", options);
    return options;
  }

  const pvalueSettings = useCallback(() => {
    let traceOptions: string[] = [];
    if (config.plotType === 'scatter' && plotLayers.length > 0) {
      traceOptions = Array.from(new Set(plotLayers[0].x));
    } else {
      traceOptions = plotLayers.map((trace: any) => trace.name);
    }
  
    return (
      <>
        {/* Initial Section */}
        <Grid container spacing={2} sx={{ marginTop: '20px', marginBottom: '20px' }}>

          <Grid item xs={2}>
          </Grid>

          <Grid item xs={10} display="flex" justifyContent="flex-start" alignItems="left">
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={config.pvalue !== undefined}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      handleChange('pvalue', event.target.checked ? 'control' : undefined);
                    }}
                  />
                }
                label="Enabled"
              />
            </FormGroup>
          </Grid>

          <Grid item xs={2}>
          </Grid>

          <Grid item xs={10} display="flex" justifyContent="flex-start" alignItems="center">
            <FormControl sx={{width: '75%', }}>
              <InputLabel>Control</InputLabel>
              <Select
                value={traceOptions.includes(config.pvalueControl || '') ? config.pvalueControl : traceOptions[0]}
                label="Negative Control"
                error={!traceOptions.includes(config.pvalueControl || '')}
                // renderValue={(value) =>
                //   traceOptions.includes(value) ? value : `⚠️  - ${value}`
                // }
                onChange={(event) => handleChange('pvalueControl', event.target.value)}
              >
                {traceOptions.map((trace, index) => (
                  <MenuItem value={trace} key={index}>
                    {trace}
                  </MenuItem>
                ))}
              </Select>
              {/* <FormHelperText>
                {traceOptions.includes(config.pvalueControl || '')
                  ? ''
                  : 'Selected Control Not Present on Plot'}
              </FormHelperText> */}
            </FormControl>
          </Grid>
        </Grid>  

        {/* Method Section */}
        <Divider sx={{ marginTop: 2, borderBottomWidth: 1, borderColor: 'primary.main' }} />
        <Grid container spacing={2} sx={{ marginTop: '20px', marginBottom: '40px' }}>
          <Grid item xs={2}>
          </Grid>
        
          <Grid item xs={10} display="flex" justifyContent="flex-start" alignItems="center">
            <FormControl sx={{width: '75%', }}>
              <InputLabel>Method</InputLabel>
              <Select
                value={config.pvalueMethod}
                label="Method"
                onChange={(event) => handleChange('pvalueMethod', event.target.value)}
              >
                <MenuItem value="ttest">Independent t-Test</MenuItem>
                <MenuItem value="kwtest">Kruskal-Wallis w/ Dunn Post-hoc</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {config.pvalueMethod === 'kwtest' ? <>
            <Grid item xs={2}>
            </Grid>
            
            <Grid item xs={9}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={config.showKWResult}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        handleChange('showKWResult', event.target.checked);
                      }}
                    />
                  }
                  label="Show Kruskal-Wallis Result"
                />
              </FormGroup>
            </Grid></>: <></>
          }

          <Grid item xs={2}>
          </Grid>
        </Grid>
        
        {/* Display Options Section */}
        <Divider sx={{ marginTop: 2, borderBottomWidth: 1, borderColor: 'primary.main' }} />
        <Grid container spacing={2} sx={{ marginTop: '20px' }}>
          
          <Grid item xs={2}>
          </Grid>

          <Grid item xs={3}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={config.pvalueHideInsignificant}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      handleChange('pvalueHideInsignificant', event.target.checked);
                    }}
                  />
                }
                label="Hide Insignificant"
              />
            </FormGroup>
          </Grid>

          <Grid item xs={6}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={config.pvalueLegend}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      handleChange('pvalueLegend', event.target.checked);
                    }}
                  />
                }
                label="Show Legend"
              />
            </FormGroup>
          </Grid>

          {/* <Grid item xs={3}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={config.pvalueUseRaw}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      handleChange('pvalueUseRaw', event.target.checked);
                    }}
                  />
                }
                label="Use Raw Data"
              />
            </FormGroup>
          </Grid> */}

          <Grid item xs={2}>
          </Grid>
            
          <Grid item xs={10}>
            <FormControl  sx={{width: '75%', marginTop: '20px' }}>
              <InputLabel>Display Mode</InputLabel>
              <Select
                value={config.pvalueDisplayMode}
                label="p-value Display Mode"
                fullWidth
                onChange={(event) => handleChange('pvalueDisplayMode', event.target.value)}
              >
                <MenuItem value="text">Only Labels</MenuItem>
                <MenuItem value="lines+text">Lines and Labels</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

      </>
    );
  }, [config, plotLayers, handleChange]);
  
  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setTab(newValue);
  };

  const getPlotLayersSorted = useCallback(() => {

    const unchangedTraces = plotLayers.filter((trace: any) => !trace.name?.endsWith(' ')); // traces ending with space correspond to scatter traces accompanying box plots

    const tracesToSort = plotLayers.filter((trace: any) => trace.name?.endsWith(' '));
    const sortedTraces = tracesToSort.sort((a, b) => a.name.localeCompare(b.name));

    // Remove subsequent traces in unchangedTraces with duplicate names and types - this is the case for example when tiling by Gene and showing a bar plot with treatment and dose on the x-axis
    const seenTraces = new Set(); // Tracks "name|type" combinations
    const seenTypes = unchangedTraces.reduce((acc, trace) => {
      acc.add(trace.type);
      return acc
    }, new Set());
    let filteredUnchangedTraces = unchangedTraces.filter((trace: any) => {
      let tempType = trace.type;
      if (seenTypes.has('box') && trace.type === 'bar') {
        tempType = 'box';
      }
      const uniqueKey = `${trace.name}|${tempType}`;
      
      if (seenTraces.has(uniqueKey)) {
          return false; // Exclude if the combination has been seen before or if the trace is tagged as a subplot
      }
      seenTraces.add(uniqueKey); // Record the combination
      return true; // Keep if not seen
    });

    // Now remove any remaining traces that have the " SP " subplot notation
    filteredUnchangedTraces = filteredUnchangedTraces.filter((trace: any) => !trace.name.includes(' SP '));

    // Combine unsorted and sorted traces
    const modifiedTraces = [...filteredUnchangedTraces, ...sortedTraces];
    const overrideColors = new Set(Object.values(config.colorOverride || {}));
    const includeOverride = overrideColors.size >= Object.values(defaultColorMap).length - 4; 
    const filterDefaultColorMap = (Object.values(defaultColorMap) as string[]).filter(col => !overrideColors.has(col) || includeOverride) as string[];
    
    let colorIndex = 0;
    let lastTraceType: string;
    let lastXAxis: string;
    let lastYAxis: string;
    const batchedOverrideUpdate: Record<string, string> = {}
    // console.log("filterdefault", filterDefaultColorMap)
    // console.log("override", config.colorOverride)
    const rows = modifiedTraces.map((trace: any, index: number) => {
      
      if (seenTypes.has('box') && lastTraceType === 'box' && trace.type === 'scatter' && trace.name[trace.name.length - 1] === ' ') {
        colorIndex = 0;
      }
      // if (lastXAxis !== trace.xaxis || lastYAxis !== trace.yaxis) {
      //   colorIndex = 0;
      // }
      lastTraceType = trace.type;
      lastXAxis = trace.xaxis;
      lastYAxis = trace.yaxis;
      let colorMapIndex: number = colorIndex % Object.keys(filterDefaultColorMap).length;
      
      const traceNameClean = (seenTypes.has('box') && trace.type == 'scatter') ? trace.name.trim() : trace.name;
      const traceTypeClean = (seenTypes.has('box') && trace.type === 'bar') ? 'box' : trace.type;
      const {color, override} = getTraceColor(colorMapIndex, traceNameClean, filterDefaultColorMap, trace); // get color from config.colorMap or defaultColorMap
      if (!override) {  
        colorIndex += 1;
      } 

      
      if (config.colorOverride){
        if (!(traceNameClean in config.colorOverride || traceNameClean in batchedOverrideUpdate))  {
          batchedOverrideUpdate[traceNameClean] = color;
        }
      }
      else {
        if (!(traceNameClean in batchedOverrideUpdate)) {
          batchedOverrideUpdate[traceNameClean] = color;
        }
      }
    

      return ({
        id: index,
        x: trace.x,
        name: traceNameClean,
        type: traceTypeClean,
        color: color,
        override: override, 
        colorMapIndex: !override ? colorMapIndex : 'Lock', // index in colorMap, used for reverse lookup
        symbol: config.symbolMap ? config.symbolMap[index] : '',
        fill: config.fillMap ? config.fillMap[index] : '',
      })
    });
    if (!deepEqual({...config.colorOverride, ...batchedOverrideUpdate}, {...config.colorOverride})) {
      handleChange('colorOverride', {...config.colorOverride, ...batchedOverrideUpdate});
    }
    return rows;
  }, [config.colorMap, config.symbolMap, config.fillMap, plotLayers, config.colorOverride, getTraceColor]);

  const plotLayersSorted = useMemo(() => getPlotLayersSorted(), [getPlotLayersSorted])

  const CustomToolbar: React.FC = () => {
    return (
        <Box display="flex" flexDirection="row" justifyContent="center">
            <Box>
                <GridToolbarQuickFilter
                    sx={{ margin: 1.1, width: '300px' }} 
                />
            </Box>
        </Box>
    );
  };

  // Note that Dialog component must have closeAfterTransition set to false as workaround for 'aria-hidden' related error being triggered on Dialog close
  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth closeAfterTransition={false}>
      <DialogTitle>Plot Options</DialogTitle>
      <DialogContent sx={{minHeight: "600px"}}>
        <Divider variant="middle" flexItem />
          <Tabs value={tab} onChange={handleTabChange}>
            <Tab label="General" />
            <Tab label="Axes" />
            <Tab label="Colors" />
            <Tab label="Statistics" />
            <Tab label="Annotations" />
            <Tab label="Tiling" />
          </Tabs>

          {tab === 0 && ( // General
            <>
              {/* Top Level Section */}
              {/* <Divider sx={{ marginTop: 2, borderBottomWidth: 1, borderColor: 'primary.main' }} /> */}
              <Grid container spacing={2} sx={{ marginTop: '20px', marginBottom: '20px' }}> 

                <Grid item xs={2}>
                </Grid>

                <Grid item xs={8} sx={{ marginTop: '10px' }}>
                  <TextField
                    label="Title"
                    variant="outlined"
                    sx={{width: '100%'}}
                    defaultValue={config.title}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      debouncedChange('title', event.target.value)
                    }
                  />
                </Grid>

                <Grid item xs={2}>
                </Grid>

                <Grid item xs={2}>
                </Grid>

                <Grid item xs={2} sx={{ marginTop: '10px' }}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={config.showTitle}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            handleChange('showTitle', event.target.checked)
                          }
                        />
                      }
                      label="Show Title"
                    />
                  </FormGroup>
                </Grid>

                <Grid item xs={2} sx={{ marginTop: '10px' }}>
                  <TextField
                    label="Title Font Size"
                    variant="outlined"
                    type="number"
                    sx={{width: '100%'}}
                    defaultValue={config.titleFontSize}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      debouncedChange('titleFontSize', parseInt(event.target.value))
                    }
                  />
                </Grid>

                
              </Grid>

              {/* Legend Section */}
              <Divider sx={{ marginTop: 2, borderBottomWidth: 1, borderColor: 'primary.main' }} />
              <Grid container spacing={2} sx={{ marginTop: '0px', marginBottom: '0px' }}> 

                <Grid item xs={2}>
                </Grid>

                <Grid item xs={3} sx={{ marginTop: '0px' }}>
                  <FormControl sx={{width: '100%', }}>
                    <InputLabel>Plot Type</InputLabel>
                    <Select
                      value={config.plotType}
                      label="Plot Type"
                      // error={}
                      // renderValue={(value) => }
                      onChange={(event) => handleChange('plotType', event.target.value)}
                    >
                      {plotTypeOptions.map((plotType) => (
                        <MenuItem value={plotType.value} key={plotType.value}>
                          {plotType.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={2}>
                </Grid>

                <Grid item xs={5} sx={{ marginTop: '0px' }}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={config.showLegend}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            handleChange('showLegend', event.target.checked)
                          }
                        />
                      }
                      label="Show Legend"
                    />
                  </FormGroup>
                </Grid>

              </Grid>

              {/* Font Section */}
              <Divider sx={{ marginTop: 2, borderBottomWidth: 1, borderColor: 'primary.main' }} />
              <Grid container spacing={2} sx={{ marginTop: '10px', marginBottom: '20px' }}> 

                <Grid item xs={2}>
                </Grid>

                <Grid item xs={3} sx={{ marginTop: '10px' }}>
                  <TextField
                    label="Default Font Size"
                    variant="outlined"
                    type="number"
                    // sx={{width: '200px'}}
                    defaultValue={config.fontSize}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      debouncedChange('fontSize', parseInt(event.target.value))
                    }
                  />
                </Grid>
              </Grid>

              {/* Plot Specific Section */}
              <Divider sx={{ marginTop: 2, borderBottomWidth: 1, borderColor: 'primary.main' }} />
              <>
                {config.plotType === 'scatter' || config.plotType === 'line' && (
                  <Grid container spacing={2} sx={{marginTop: '10px'}}>

                    <Grid item xs={2}>
                    </Grid>

                    <Grid item xs={2}>
                      <TextField
                        label="Marker Size"
                        variant="outlined"
                        value={config.markerSize}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          handleChange('markerSize', parseFloat(event.target.value))
                        }
                        type="number"
                      />
                    </Grid>

                    <Grid item xs={3}>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={config.scatterShowLabels}
                              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                handleChange('scatterShowLabels', event.target.checked)
                              }
                            />
                          }
                          label="Show Text Labels"
                        />
                      </FormGroup>
                    </Grid>

                    <Grid item xs={5} display="flex" justifyContent="flex-start" alignItems="center">
                      <FormControl sx={{width: '75%', }}>                  
                        <InputLabel shrink={true}>Label Scatter Points By</InputLabel>
                        <Select
                          value={colorScatterByOptions().includes(config.scatterLabelBy || '') ? config.scatterLabelBy : ''}
                          displayEmpty
                          onChange={(event) => handleChange('scatterLabelBy', event.target.value)}
                          label="Label Scatter Points By"
                          placeholder='Same as color by'
                        >
                          <MenuItem value="" key={''}>
                            <em>Same as color by</em>
                          </MenuItem>
                          {colorScatterByOptions().map((option, index) => (
                            <MenuItem value={option} key={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={2}>
                    </Grid>
                    {config.plotType === 'line' && (
                      <Grid item xs={5} display="flex" justifyContent="flex-start" alignItems="center">
                        <FormControl sx={{width: '75%', }}>                  
                          <InputLabel shrink={true}>Connect Lines By</InputLabel>
                          <Select
                            value={colorScatterByOptions().includes(config.scatterColorBy || '') ? config.scatterColorBy : ''}
                            displayEmpty
                            onChange={(event) => handleChange('scatterColorBy', event.target.value)}
                            label="Connect Lines By"
                            // placeholder='Same as color by'
                          >
                            <MenuItem value="" key={''}>
                              <em>Same as traces</em>
                            </MenuItem>
                            {colorScatterByOptions().map((option, index) => (
                              <MenuItem value={option} key={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                  </Grid>
                )}

                

                {config.plotType === 'box' && (
                  <Grid container spacing={2} sx={{marginTop: '10px'}}>

                    <Grid item xs={2}>
                    </Grid>

                    {/* <Grid item xs={10}>
                      <Box sx={{marginTop: "10px"}}>
                        <Typography variant='button'>BOX PLOT OPTIONS:</Typography>
                      </Box>
                    </Grid> */}

                    <Grid item xs={2}>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={config.boxpoints === 'all'}
                              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                handleChange('boxpoints', event.target.checked ? 'all' : undefined)
                              }
                            />
                          }
                          label="Points"
                        />
                      </FormGroup>
                    </Grid>

                    <Grid item xs={2}>
                      <TextField
                        label="Marker Size"
                        variant="outlined"
                        value={config.markerSize}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          handleChange('markerSize', parseFloat(event.target.value))
                        }
                        type="number"
                      />
                    </Grid>

                    <Grid item xs={2}>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={config.scatterShowLabels}
                              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                handleChange('scatterShowLabels', event.target.checked)
                              }
                            />
                          }
                          label="Labels"
                        />
                      </FormGroup>
                    </Grid>
                    
                    <Grid item xs={2}>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={config.boxMeanLine}
                              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                handleChange('boxMeanLine', event.target.checked)
                              }
                            />
                          }
                          label="Mean Line"
                        />
                      </FormGroup>
                    </Grid>
                    
                  </Grid>
                )}

                {config.plotType === 'bar' && (
                  <Grid container spacing={2} sx={{marginTop: '10px'}}>
                    
                    <Grid item xs={2}>
                    </Grid>

                    <Grid item xs={3}>
                      <Box sx={{marginTop: "10px"}}>
                        <Typography variant='button'>BAR PLOT OPTIONS:</Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={3}>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={config.errorBars}
                              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                handleChange('errorBars', event.target.checked)
                              }
                            />
                          }
                          label="Standard Error Bars"
                        />
                      </FormGroup>
                    </Grid>
                    <Grid item xs={3}>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={config.onesidedErrorBars}
                              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                handleChange('onesidedErrorBars', event.target.checked)
                              }
                            />
                          }
                          label="Onesided Error Bars"
                        />
                      </FormGroup>
                    </Grid>
                    {/* <Grid item xs={2}>
                    </Grid> */}
                    <Grid item xs={1}>
                    </Grid>
                    <Grid item xs={2}>
                    </Grid>
                    <Grid item xs={3} sx={{ marginTop: '5px' }}>
                      <FormControl sx={{width: '100%', }}>
                        <InputLabel>Bar Mode</InputLabel>
                        <Select
                          value={config.barMode}
                          label="Bar Mode"
                          defaultValue='none'
                          // error={}
                          // renderValue={(value) => }
                          onChange={(event) => {
                            handleChange('barMode', event.target.value)
                            if (event.target.value === 'none') {
                              handleChange('barGroupBy', '');
                            }
                            if (event.target.value === 'stack' || event.target.value === 'group') {
                              handleChange('barGroupBy', colorScatterByOptions()[0]);
                              handleChange('showLegend', true);
                            }
                          }}
                        >
                          <MenuItem value={'none'} key={'none'}>
                            <em>Off</em>
                          </MenuItem>
                          <MenuItem value={'group'} key={'group'}>
                            Group
                          </MenuItem>
                          <MenuItem value={'stack'} key={'stack'}>
                            Stack
                          </MenuItem>
                          <MenuItem value={'relative'} key={'relative'}>
                            Percent Stack
                          </MenuItem>
                          
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={5} display="flex" justifyContent="flex-start" alignItems="center">
                        <FormControl sx={{width: '75%', }}>                  
                          <InputLabel shrink={true}>Divide Bars By</InputLabel>
                          <Select
                            value={colorScatterByOptions().includes(config.barGroupBy || '') ? config.barGroupBy : ''}
                            displayEmpty
                            disabled={config.barMode === 'none'}
                            onChange={(event) => handleChange('barGroupBy', event.target.value)}
                            label="Divide Bars By"
                            // placeholder='Same as color by'
                          >
                            <MenuItem value="" key={'Same as traces'}>
                              <em>Same as traces</em>
                            </MenuItem>
                            {colorScatterByOptions().map((option, index) => (
                              <MenuItem value={option} key={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    
                  </Grid>
                )}

                {config.plotType === 'heatmap' && (
                  <Grid container spacing={2} sx={{marginTop: '10px'}}>
                    
                    <Grid item xs={2}>
                    </Grid>

                    <Grid item xs={10}>
                      <Box sx={{marginTop: "10px"}}>
                        <Typography variant='button'>HEATMAP PLOT OPTIONS:</Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={2}>
                    </Grid>

                    <Grid item xs={2} sx={{ marginTop: '0px' }}>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={config.heatmapXcluster}
                              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                handleChange('heatmapXcluster', event.target.checked)
                              }
                            />
                          }
                          label="Cluster X"
                        />
                      </FormGroup>
                    </Grid>

                    <Grid item xs={2} sx={{ marginTop: '0px' }}>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={config.heatmapYcluster}
                              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                handleChange('heatmapYcluster', event.target.checked)
                              }
                            />
                          }
                          label="Cluster Y"
                        />
                      </FormGroup>
                    </Grid>

                    <Grid item xs={3} sx={{ marginTop: '0px' }}>
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={config.heatmapLog2}
                              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                handleChange('heatmapLog2', event.target.checked)
                              }
                            />
                          }
                          label="Log2 Transform"
                        />
                      </FormGroup>
                    </Grid>

                    <Grid item xs={2}>
                    </Grid>
                    <Grid item xs={2}>
                    </Grid>

                    <Grid item xs={4} sx={{ marginTop: '5px' }}>
                      <FormControl sx={{width: '100%', }}>
                        <InputLabel>Handle Filtered Values</InputLabel>
                        <Select
                          value={config.heatmapHandleFiltered || 'keepRows'}
                          label="Remove Missing Values"
                          // error={}
                          // renderValue={(value) => }
                          onChange={(event) => {
                            const val = event.target.value;
                            handleChange('heatmapHandleFiltered', val)
                          }}
                        >
                          <MenuItem value={'none'} key={'none'}>
                            Show as Blanks
                          </MenuItem>
                          <MenuItem value={'keepRows'} key={'keepRows'}>
                            Keep if Any Row Matches
                          </MenuItem>
                          <MenuItem value={'keepCols'} key={'keepCols'}>
                            Keep if Any Column Matches
                          </MenuItem>
                          <MenuItem value={'rows'} key={'rows'}>
                            Remove By Row
                          </MenuItem>
                          <MenuItem value={'columns'} key={'columns'}>
                            Remove By Column
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={2}>
                    </Grid>
                    <Grid item xs={2}>
                    </Grid>

                    <Grid item xs={2}>
                    </Grid>
                    <Grid item xs={2}>
                    </Grid>

                    <Grid item xs={3} sx={{ marginTop: '5px' }}>
                      <TextField
                        label="Z-Axis Min"
                        variant="outlined"
                        type="number"
                        fullWidth
                        defaultValue={config.zmin !== undefined ? config.zmin : null}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          debouncedChange('zmin', parseFloat(event.target.value))
                        }
                      />
                    </Grid>

                    <Grid item xs={3} sx={{ marginTop: '5px' }}>
                      <TextField
                        label="Z-Axis Max"
                        variant="outlined"
                        type="number"
                        fullWidth
                        defaultValue={config.zmax !== undefined ? config.zmax : null}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          debouncedChange('zmax', parseFloat(event.target.value))
                        }
                      />
                    </Grid>

                    <Grid item xs={3} sx={{ marginTop: '5px' }}>
                      <FormControl sx={{width: '100%', }}>
                        <InputLabel>Colorscale</InputLabel>
                        <Select
                          value={config.colorscale}
                          label="Colorscale"
                          // error={}
                          // renderValue={(value) => }
                          onChange={(event) => handleChange('colorscale', event.target.value)}
                        >
                          {colorScaleOptions.map((option: any) => (
                            <MenuItem value={option.value} key={option.label}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={1}>
                    </Grid>

                    <Grid item xs={2}>
                    </Grid>

                    <Grid item xs={3} sx={{ marginTop: '5px' }}>
                      <TextField
                        label="Z-Axis Min Rel"
                        variant="outlined"
                        type="number"
                        fullWidth
                        defaultValue={config.zmin_rel !== undefined ? config.zmin_rel : null}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          debouncedChange('zmin_rel', parseFloat(event.target.value))
                        }
                      />
                    </Grid>

                    <Grid item xs={3} sx={{ marginTop: '5px' }}>
                      <TextField
                        label="Z-Axis Max Rel"
                        variant="outlined"
                        type="number"
                        fullWidth
                        defaultValue={config.zmax_rel !== undefined ? config.zmax_rel : null}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          debouncedChange('zmax_rel', parseFloat(event.target.value))
                        }
                      />
                    </Grid>

                    <Grid item xs={4}>
                    </Grid>

                    <Grid item xs={2}>
                    </Grid>

                    <Grid item xs={3} sx={{ marginTop: '5px' }}>
                      <TextField
                        label="Z-Axis Min Log2 "
                        variant="outlined"
                        type="number"
                        fullWidth
                        defaultValue={config.zmin_log2 !== undefined ? config.zmin_log2 : null}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          debouncedChange('zmin_log2', parseFloat(event.target.value))
                        }
                      />
                    </Grid>

                    <Grid item xs={3} sx={{ marginTop: '5px' }}>
                      <TextField
                        label="Z-Axis Max Log2"
                        variant="outlined"
                        type="number"
                        fullWidth
                        defaultValue={config.zmax_log2 !== undefined ? config.zmax_log2 : null}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          debouncedChange('zmax_log2', parseFloat(event.target.value))
                        }
                      />
                    </Grid>

                    <Grid item xs={4}>
                    </Grid>

                    <Grid item xs={2}>
                    </Grid>
                    
                    <Grid item xs={8} sx={{ marginTop: '10px' }}>
                      <TextField
                        label="Colorscale Title"
                        variant="outlined"
                        sx={{width: '100%'}}
                        defaultValue={config.colorscaleTitle}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          debouncedChange('colorscaleTitle', event.target.value)
                        }
                      />
                    </Grid>

                    <Grid item xs={1}>
                    </Grid>

                  </Grid>
                )}
              </>
            </>
          )}
          
          {tab === 1 && ( // Axes
            <>
              {/* X Axis Section */}
              {/* <Divider sx={{ marginTop: 2, borderBottomWidth: 1, borderColor: 'primary.main' }} /> */}
              <Grid container spacing={2} sx={{marginTop: '10px'}}>
                <Grid item xs={2} >
                  <Typography variant='body1' style={{textAlign:'center', marginTop:'20px',  }}><b>X-Axis Options:</b></Typography>
                </Grid>
                <Grid item xs={4} sx={{ marginTop: '5px' }}>
                  <TextField
                    label="Title"
                    variant="outlined"
                    disabled={config.xaxis_autotitle}
                    helperText={config.xaxis_autotitle ? "Disable X Auto Title to edit" : ''}
                    sx={{width: '100%'}}
                    defaultValue={config.xtitle}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      debouncedChange('xtitle', event.target.value)
                    }
                  />
                </Grid>

                <Grid item xs={2} sx={{ marginTop: '5px' }}>
                  <TextField
                    label="Title Size"
                    variant="outlined"
                    type="number"
                    sx={{width: '100%'}}
                    defaultValue={config.xAxisTitleFontSize}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      debouncedChange('xAxisTitleFontSize', parseInt(event.target.value))
                    }
                  />
                </Grid>
                <Grid item xs={3} sx={{ marginTop: '5px' }}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={config.xaxis_autotitle}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            handleChange('xaxis_autotitle', event.target.checked)
                          }
                        />
                      }
                      label="X Auto Title"
                    />
                  </FormGroup>
                </Grid>
                <Grid item xs={2}>
                </Grid>

                <Grid item xs={3} sx={{ marginTop: '5px' }}>
                  <TextField
                    label="Min"
                    variant="outlined"
                    type="number"
                    fullWidth
                    defaultValue={config.xmin !== undefined ? config.xmin : null}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      debouncedChange('xmin', parseFloat(event.target.value))
                    }
                  />
                </Grid>

                <Grid item xs={3} sx={{ marginTop: '5px' }}>
                  <TextField
                    label="Max"
                    variant="outlined"
                    type="number"
                    fullWidth
                    defaultValue={config.xmax !== undefined ? config.xmax : null}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      debouncedChange('xmax', parseFloat(event.target.value))
                    }
                  />
                </Grid>

                <Grid item xs={3}>
                  <FormGroup sx={{ marginTop: '15px' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={config.xaxis_type === 'log'}
                          onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            handleChange('xaxis_type', event.target.checked ? 'log' : 'lin');
                          }}
                        />
                      }
                      label="Log Scale"
                    />
                  </FormGroup>
                </Grid>

                <Grid item xs={1}>
                </Grid>

                <Grid item xs={2}>
                </Grid>

                <Grid item xs={3} sx={{ marginTop: '5px' }}>
                  <TextField
                    label="Min Relative Mode"
                    variant="outlined"
                    type="number"
                    fullWidth
                    defaultValue={config.xmin_rel !== undefined ? config.xmin_rel : null}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      debouncedChange('xmin_rel', parseFloat(event.target.value))
                    }
                  />
                </Grid>

                <Grid item xs={3} sx={{ marginTop: '5px' }}>
                  <TextField
                    label="Max Relative Mode"
                    variant="outlined"
                    type="number"
                    fullWidth
                    defaultValue={config.xmax_rel !== undefined ? config.xmax_rel : null}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      debouncedChange('xmax_rel', parseFloat(event.target.value))
                    }
                  />
                </Grid>

                <Grid item xs={4}>
                </Grid>

                <Grid item xs={2}>
                </Grid>

                <Grid item xs={3} sx={{ marginTop: '5px' }}>
                  <TextField
                    label="X Label Font Size"
                    variant="outlined"
                    type="number"
                    sx={{width: '100%'}}
                    defaultValue={config.xAxisLabelFontSize}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      debouncedChange('xAxisLabelFontSize', parseInt(event.target.value))
                    }
                  />
                </Grid>

                <Grid item xs={3} sx={{ marginTop: '5px' }}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={config.xaxis_showline}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            handleChange('xaxis_showline', event.target.checked)
                          }
                        />
                      }
                      label="X Axis Line"
                    />
                  </FormGroup>
                </Grid>

                <Grid item xs={3} sx={{ marginTop: '5px' }}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={config.xaxis_showgrid}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            handleChange('xaxis_showgrid', event.target.checked)
                          }
                        />
                      }
                      label="X Axis Grid"
                    />
                  </FormGroup>
                </Grid>

                <Grid item xs={2}>
                </Grid>

                <Grid item xs={3} sx={{ marginTop: '5px' }}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={config.showXtickLabels}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            handleChange('showXtickLabels', event.target.checked)
                          }
                        />
                      }
                      label="Show X Tick Labels"
                    />
                  </FormGroup>
                </Grid>
                

              </Grid>

              {/* Y Axis Section */}
              <Divider sx={{ marginTop: 2, borderBottomWidth: 1, borderColor: 'primary.main' }} />
              <Grid container spacing={2} sx={{marginTop: '10px'}}>

                <Grid item xs={2}>                
                  <Typography variant='body1' style={{textAlign:'center', marginTop:'20px',  }}><b>Y-Axis Options:</b></Typography>
                </Grid>
                
                <Grid item xs={4} sx={{ marginTop: '5px' }}>
                  <TextField
                    label="Title"
                    variant="outlined"
                    disabled={config.yaxis_autotitle}
                    helperText={(config.yaxis_autotitle) ? "Disable Y Auto Title to edit" : ''}
                    sx={{width: '100%'}}
                    defaultValue={config.ytitle}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      debouncedChange('ytitle', event.target.value)
                    }
                  />
                </Grid>

                <Grid item xs={2} sx={{ marginTop: '5px' }}>
                  <TextField
                    label="Title Size"
                    variant="outlined"
                    type="number"
                    sx={{width: '100%'}}
                    defaultValue={config.yAxisTitleFontSize}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      debouncedChange('yAxisTitleFontSize', parseInt(event.target.value))
                    }
                  />
                </Grid>
                <Grid item xs={3} sx={{ marginTop: '5px' }}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={config.yaxis_autotitle}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            handleChange('yaxis_autotitle', event.target.checked)
                          }
                        />
                      }
                      label="Y Auto Title"
                    />
                  </FormGroup>
                </Grid>
                <Grid item xs={2}>
                </Grid>

                <Grid item xs={3} sx={{ marginTop: '5px' }}>
                  <TextField
                    label="Min"
                    variant="outlined"
                    type="number"
                    fullWidth
                    // defaultValue={config.ymin !== undefined ? config.ymin : null}
                    defaultValue={config.ymin ?? null}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      const value = parseFloat(event.target.value);
                      handleChange('ymin', isNaN(value) ? null : value);
                    }}
                  />
                </Grid>

                <Grid item xs={3} sx={{ marginTop: '5px' }}>
                  <TextField
                    label="Max"
                    variant="outlined"
                    type="number"
                    fullWidth
                    // defaultValue={config.ymax !== undefined ? config.ymax : null}
                    defaultValue={config.ymax ?? null}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      const value = parseFloat(event.target.value);
                      handleChange('ymax', isNaN(value) ? null : value);
                    }}
                  />
                </Grid>

                <Grid item xs={3}>
                  <FormGroup sx={{ marginTop: '15px' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={config.yaxis_type === 'log'}
                          onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            handleChange('yaxis_type', event.target.checked ? 'log' : 'lin');
                          }}
                        />
                      }
                      label="Log Scale"
                    />
                  </FormGroup>
                </Grid>

                <Grid item xs={1}>
                </Grid>

                <Grid item xs={2}>
                </Grid>

                <Grid item xs={3} sx={{ marginTop: '5px' }}>
                  <TextField
                    label="Min Relative Mode"
                    variant="outlined"
                    type="number"
                    fullWidth
                    defaultValue={config.ymin_rel ?? null}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      const value = parseFloat(event.target.value);
                      handleChange('ymin_rel', isNaN(value) ? null : value);
                    }}
                  />
                </Grid>

                <Grid item xs={3} sx={{ marginTop: '5px' }}>
                  <TextField
                    label="Max Relative Mode"
                    variant="outlined"
                    type="number"
                    fullWidth
                    // defaultValue={config.ymax_rel !== undefined ? config.ymax_rel : null}
                    defaultValue={config.ymax_rel ?? null}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      const value = parseFloat(event.target.value);
                      handleChange('ymax_rel', isNaN(value) ? null : value);
                    }}
                  />
                </Grid>

                <Grid item xs={4}>
                </Grid>

                <Grid item xs={2}>
                </Grid>
                
                <Grid item xs={3} sx={{ marginTop: '5px' }}>
                  <TextField
                    label="Y Label Font Size"
                    variant="outlined"
                    type="number"
                    sx={{width: '100%'}}
                    defaultValue={config.yAxisLabelFontSize}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      debouncedChange('yAxisLabelFontSize', parseInt(event.target.value))
                    }
                  />
                </Grid>

                <Grid item xs={3} sx={{ marginTop: '5px' }}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={config.yaxis_showline}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            handleChange('yaxis_showline', event.target.checked)
                          }
                        />
                      }
                      label="Y Axis Line"
                    />
                  </FormGroup>
                </Grid>

                <Grid item xs={3} sx={{ marginTop: '5px' }}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={config.yaxis_showgrid}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            handleChange('yaxis_showgrid', event.target.checked)
                          }
                        />
                      }
                      label="Y Axis Grid"
                    />
                  </FormGroup>
                </Grid>
                
                <Grid item xs={2}>
                </Grid>

                <Grid item xs={3} sx={{ marginTop: '5px' }}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={config.showYtickLabels}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            handleChange('showYtickLabels', event.target.checked)
                          }
                        />
                      }
                      label="Show Y Tick Labels"
                    />
                  </FormGroup>
                </Grid>

              </Grid>

              {/* General Section */}
              {/* <Divider sx={{ marginTop: 2, borderBottomWidth: 1, borderColor: 'primary.main' }} />
              <Grid container spacing={2} sx={{marginTop: '20px'}}>
                
                <Grid item xs={2}>
                </Grid>
              </Grid> */}

            </>
          )}

          {tab === 2 && ( // Colors
            <>
              <Grid container spacing={3} sx={{marginTop: '20px'}}>
                <Grid item xs={12}>
                  <div style={{ display: 'flex', height: '100%', maxHeight: '300px', width: '100%'}}>
                    <div style={{ flexGrow: 1, height: '100%' }}>
                      <DataGrid
                        // rows={plotLayers.filter((trace: any) => !trace.name?.endsWith(' ')).map((trace: any, index: number) => ({
                        rows={plotLayersSorted}
                        columns={traceColumns}
                        // autoHeight
                        slots={{ toolbar: CustomToolbar }}
                        hideFooter
                        disableColumnFilter
                        disableColumnSelector
                        disableDensitySelector
                        density="compact"
                      />
                    </div>
                  </div>
                </Grid>

                <Grid item xs={2}>
                </Grid>

                <Grid item xs={2} sx={{marginTop: '20px'}} display="flex" justifyContent="flex-start" alignItems="left" >
                  <MuiColorInput
                    label="Fixed Color"
                    value={config.fixedColor}
                    format="hex"
                    onChange={(color: string) => handleChange('fixedColor', color)}
                  />
                </Grid>

                <Grid item xs={8} sx={{marginTop: '20px'}} display="flex" justifyContent="flex-start" alignItems="left">
                  <FormGroup sx={{ marginTop: '10px' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={config.fixedColorMode}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            handleChange('fixedColorMode', event.target.checked)
                          }
                        />
                      }
                      label="Enable Fixed Color"
                    />
                  </FormGroup>
                </Grid>
              </Grid>

              {/* Scatter Plot Color By Controls */}
              <Divider sx={{ marginTop: 2, borderBottomWidth: 1, borderColor: 'primary.main' }} />
              <Grid container spacing={3} sx={{marginTop: '20px', marginBottom: '30px'}}>
                
                <Grid item xs={2}>
                </Grid>

                <Grid item xs={10} display="flex" justifyContent="flex-start" alignItems="center">
                  <FormControl sx={{width: '75%', }}>                  
                    <InputLabel shrink={true}>Color Scatter Points By</InputLabel>
                    <Select
                      value={colorScatterByOptions().includes(config.scatterColorBy || '') ? config.scatterColorBy : ''}
                      displayEmpty
                      onChange={(event) => handleChange('scatterColorBy', event.target.value)}
                      label="Color Scatter Points By"
                      placeholder='Same as traces'
                    >
                      <MenuItem value="" key={''}>
                        <em>Same as traces</em>
                      </MenuItem>
                      {colorScatterByOptions().map((option, index) => (
                        <MenuItem value={option} key={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

              </Grid>


              {/* Other Color Controls */}
              <Divider sx={{ marginTop: 2, borderBottomWidth: 1, borderColor: 'primary.main' }} />
              <Grid container spacing={3} sx={{marginTop: '20px'}}>
                
                <Grid item xs={2}>
                </Grid>

                <Grid item xs={3} sx={{ marginTop: '10px' }}>
                  <MuiColorInput
                    label="Plot Background Color"
                    sx={{width: '200px'}}
                    value={config.plot_bgcolor}
                    format="hex"
                    onChange={(color: string) => handleChange('plot_bgcolor', color)}
                  />
                </Grid>

                <Grid item xs={3} sx={{ marginTop: '10px' }} display="flex" justifyContent="flex-start" alignItems="left">
                  <MuiColorInput
                    label="Axis Line Color"
                    value={config.axis_linecolor}
                    format="hex"
                    onChange={(color: string) => handleChange('axis_linecolor', color)}
                  />
                </Grid>
                
                <Grid item xs={3} sx={{ marginTop: '10px' }} display="flex" justifyContent="flex-start" alignItems="left">
                  <MuiColorInput
                    label="Grid Line Color"
                    value={config.gridline_color ? config.gridline_color : '#dbe2ec' }
                    format="hex"
                    onChange={(color: string) => handleChange('gridline_color', color)}
                  />
                </Grid>


              </Grid>
            </>
          )}

          {tab === 3 && ( // Statistics
            <div>
              {pvalueSettings()}
            </div>
          )}

          {tab === 4 && ( // Annotations
            <>
              <Grid container spacing={3} sx={{marginTop: '0px'}}>
                <Grid item xs={12}>
                  <AnnotationManager
                    annotations={plotConfig.annotations}
                    shapes={plotConfig.shapes}
                    onUpdate={handleAnnotationUpdate} 
                  />
                </Grid>
              </Grid>
            </>
          )}

          {tab === 5 && ( // Tiling
            <>
              {/* // Add fields for controlling plotConfig.subplotXgap and plotConfig.subplotYgap*/}
              <Grid container spacing={3} sx={{marginTop: '20px'}}>
                <Grid item xs={2}>
                </Grid>

                <Grid item xs={3} sx={{ marginTop: '10px' }}>
                  <TextField
                    label="Subplot X Gap"
                    variant="outlined"
                    type="number"
                    fullWidth
                    defaultValue={config.subplotXgap !== undefined ? config.subplotXgap : 0}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      debouncedChange('subplotXgap', parseFloat(event.target.value))
                    }
                  />
                </Grid>

                <Grid item xs={3} sx={{ marginTop: '10px' }}>
                  <TextField
                    label="Subplot Y Gap"
                    variant="outlined"
                    type="number"
                    fullWidth
                    defaultValue={config.subplotYgap !== undefined ? config.subplotYgap : 0}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      debouncedChange('subplotYgap', parseFloat(event.target.value))
                    }
                  />
                </Grid>
                
                <Grid item xs={4}>
                </Grid>
                
                <Grid item xs={2}>
                </Grid>

                <Grid item xs={6} sx={{ marginTop: '10px' }}>
                  <RadioGroup
                      row
                      aria-labelledby="tile-mode-label"
                      name="tile-mode-radio-buttons-group"
                      value={config.tileMode ? config.tileMode : 'autoGrid'}
                      onChange={(event: ChangeEvent<HTMLInputElement>)=>{
                        handleChange('tileMode', event.target.value)
                      }}
                  >
                      <FormControlLabel value="fixedRow" control={<Radio />} label="Fixed Row" />
                      <FormControlLabel value="fixedColumn" control={<Radio />} label="Fixed Column" />
                      <FormControlLabel value="autoGrid" control={<Radio />} label="Auto Grid" />
                  </RadioGroup>
                </Grid>
                <Grid item xs={4}>
                </Grid>
                
                { config.tileMode !== 'autoGrid' && (
                  <>
                    <Grid item xs={2}>
                    </Grid>
                    
                    {config.tileMode === 'fixedRow' && <Grid item xs={3} sx={{ marginTop: '10px' }}>
                      <TextField
                        label={"Number of Rows"}
                        variant="outlined"
                        type="number"
                        fullWidth
                        // defaultValue={config.tileFixedCount !== undefined ? config.tileFixedCount : 1}
                        defaultValue={config.tileFixedRowCount ?? 1}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>{
                          const value = parseInt(event.target.value);
                          debouncedChange("tileFixedRowCount", isNaN(value) ? 1 : value);
                        }}
                      />
                    </Grid>}
                    {config.tileMode === 'fixedColumn' && <Grid item xs={3} sx={{ marginTop: '10px' }}>
                      <TextField
                        label={"Number of Columns"}
                        variant="outlined"
                        type="number"
                        fullWidth
                        // defaultValue={config.tileFixedCount !== undefined ? config.tileFixedCount : 1}
                        defaultValue={config.tileFixedColumnCount ?? 1}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>{
                          const value = parseInt(event.target.value);
                          debouncedChange("tileFixedColumnCount", isNaN(value) ? 1 : value);
                        }}
                      />
                    </Grid>}
                    
                    <Grid item xs={7}>
                    </Grid>
                  </>
                )}

                <Grid item xs={2}>
                </Grid>

                <Grid item xs={8} sx={{marginTop: '20px'}} display="flex" justifyContent="flex-start" alignItems="left">
                  <FormGroup sx={{ marginTop: '10px' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={config.showTileTitles}
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            handleChange('showTileTitles', event.target.checked)
                          }
                        />
                      }
                      label="Show Tile Titles"
                    />
                  </FormGroup>
                </Grid>
                
                <Grid item xs={4}>
                </Grid>
              </Grid>
            </>
          )}


      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={() => handleApply(false)}>Apply</Button>
        <Button disabled={disableSave} onClick={handleSavePlot}>
          Apply & Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlotOptionsModal;